import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Bar, Line} from 'react-chartjs-2';
import Cookies from 'universal-cookie';
import RadioAction from '../../utils/RadioAction';
import i18next from 'i18next';

import {BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    BarElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
);

const cookies = new Cookies();
const GRAPHS_PER_ROW_COOKIE_KEY = 'analytics-graphs-per-row';
const PREVIOUS_GRAPHS_PER_ROW_COOKIE_KEY = 'analytics-previous-graphs-per-row';

const ROW_MAPPING = {
  '1': 'col-lg-12',
  '2': 'col-lg-6',
  '3': 'col-lg-4',
};

class CustomChart extends Component {
  constructor(props) {
    super(props);

    this.onGraphsPerRowChange = this.onGraphsPerRowChange.bind(this);

    this.state = {
      graphsPerRow: '2',
    };
  }

  componentDidMount() {
    let graphsPerRow = cookies.get(GRAPHS_PER_ROW_COOKIE_KEY);
    if (!graphsPerRow) {
      cookies.set(GRAPHS_PER_ROW_COOKIE_KEY, '2', {expires: new Date(Date.now() + 31536000000)});
      graphsPerRow = cookies.get(GRAPHS_PER_ROW_COOKIE_KEY);
    }

    this.setState({
      graphsPerRow: graphsPerRow,
    });
  }

  createOptions(chartView) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      onClick: (event, elements) => {
        if (elements[0]) {
          this.onChartValueClick(chartView, elements[0].datasetIndex, elements[0].index);
        }
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            font: {
              size: 15,
            },
          },
        },
        title: {
          display: true,
          text: chartView.title.text[0].text,
          font: {
            size: 20,
          },
        },
        tooltip: {
          titleFont: {
            size: 20,
          },
          bodyFont: {
            size: 25,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            font: {
              size: 20,
            },
          },
        },
        y: {
          max: chartView.maxValue,
          min: chartView.minValue,
          ticks: {
            count: 11,
            font: {
              size: 20,
            },
          },
          title: {
            display: true,
            text: chartView.title.text[0].text,
            font: {
              size: 15,
            },
          },
        },
      },
    };
  }

  onChartValueClick(chartView, datasetIndex, index) {
    switch (chartView.type) {
      case 'SiteTerminalsWithMostUnlocks':
      case 'SiteTerminalsWithWorstBestUptime':
      case 'SiteTerminalsWithWorstBestConnectivity':
      case 'SiteTerminalsWithWorstBestWebUnlockUnlockSuccessRate':
      case 'SiteTerminalsWithWorstBestWebUnlockLatency':
      case 'SiteTerminalsWithLeastRemainingBattery':
      case 'SiteTerminalsWithWorstBestBatteryFluctuation':
      case 'SiteTerminalsWithFatalWebUnlocks':
        setTimeout(() => {
          this.onTerminalClick(chartView.labels[index].textValue);
        }, 50);
        break;
      case 'SiteTerminalUptimeHistory':
      case 'SiteTerminalWebUnlockSuccessRateHistory':
        if (datasetIndex > 0) {
          setTimeout(() => {
            this.onTerminalClick(chartView.datasets[datasetIndex].name.textValue);
          }, 50);
        }
        break;
    }
  }

  onTerminalClick(terminalId) {
    cookies.set('analytics-terminalId', terminalId, {expires: new Date(Date.now() + 31536000000)});
    this.props.triggerTabChangeFunc();
  }

  getBorderColor(datasetIndex, isRed, isGreen) {
    return isRed ?
      'rgb(' + Math.min(datasetIndex * 40 + 210, 250) + ', ' + Math.min(datasetIndex * 100 + 10, 110) + ', ' + Math.min(datasetIndex * 100 + 40, 140) + ')' :
      isGreen ?
        'rgb(' + Math.min(datasetIndex * 60, 60) + ', ' + Math.min(datasetIndex * 20 + 200, 220) + ', ' + Math.min(datasetIndex * 60, 60) + ')' :
        'rgb(' + Math.min(datasetIndex * 20 + 140, 220) + ',' + Math.min(datasetIndex * 20 + 140, 220) + ',' + Math.min(datasetIndex * 20 + 140, 220) + ')';
  }

  getBackgroundColor(datasetIndex, isRed, isGreen) {
    return isRed ?
      'rgb(' + Math.min(datasetIndex * 40 + 210, 250) + ', ' + Math.min(datasetIndex * 100 + 10, 110) + ', ' + Math.min(datasetIndex * 100 + 40, 140) + ', 0.5)' :
      isGreen ?
        'rgb(' + Math.min(datasetIndex * 60, 60) + ', ' + Math.min(datasetIndex * 20 + 200, 220) + ', ' + Math.min(datasetIndex * 60, 60) + ', 0.5)' :
        'rgba(' + Math.min(datasetIndex * 20 + 140, 220) + ',' + Math.min(datasetIndex * 20 + 140, 220) + ',' + Math.min(datasetIndex * 20 + 140, 220) + ', 0.5)';
  }

  isGreen(chartView, datasetIndex) {
    switch (chartView.type) {
      case 'SiteTerminalUnlockHistory':
      case 'TerminalUnlockHistory':
        return datasetIndex === 2 || datasetIndex === 3;
      case 'FleetFirmwareVersions':
        return this.isFirmwareUpToDate(chartView.datasets[datasetIndex].name.numericValue);
      case 'FleetUnlockHistory':
        return datasetIndex === 2;
      case 'SiteTerminalsWithMostUnlocks':
        return true;
      default:
        return datasetIndex === 0;
    }
  }

  isRed(chartView, datasetIndex, index) {
    switch (chartView.type) {
      case 'FleetUnlockHistory':
        return (datasetIndex === 0 || datasetIndex === 1) && chartView.datasets[datasetIndex].values[index].numericValue > 0;
      case 'SiteHMSUptimeHistory':
      case 'SiteTerminalUptimeHistory':
      case 'TerminalUptimeHistory':
      case 'SiteTerminalsWithWorstBestUptime':
        return datasetIndex === 0 && chartView.datasets[datasetIndex].values[index].numericValue < this.props.kpiOptions.minimumUptime;
      case 'SiteTerminalConnectivityHistory':
      case 'SiteTerminalsWithWorstBestConnectivity':
      case 'TerminalConnectivityHistory':
        return datasetIndex === 0 && chartView.datasets[datasetIndex].values[index].numericValue < this.props.kpiOptions.minimumConnectivity;
      case 'SiteTerminalWebUnlockSuccessRateHistory':
      case 'SiteTerminalsWithWorstBestWebUnlockUnlockSuccessRate':
      case 'TerminalWebUnlockSuccessRateHistory':
        return datasetIndex === 0 && chartView.datasets[datasetIndex].values[index].numericValue < this.props.kpiOptions.minimumWebUnlockSuccessRate;
      case 'SiteTerminalsWithWorstBestWebUnlockLatency':
        return datasetIndex === 0 && chartView.datasets[datasetIndex].values[index].numericValue > this.props.kpiOptions.maximumWebUnlockLatency;
      case 'SiteTerminalWebUnlockLatencyHistogram':
        return chartView.labels[index].numericValue > this.props.kpiOptions.maximumWebUnlockLatency;
      case 'SiteTerminalBatteryHistory':
      case 'TerminalBatteryHistory':
        return datasetIndex === 0 && chartView.datasets[datasetIndex].values[index].numericValue < this.props.kpiOptions.minimumBatteryPercentage;
      case 'SiteTerminalsWithLeastRemainingBattery':
        return datasetIndex === 0 && chartView.datasets[datasetIndex].values[index].numericValue < this.props.kpiOptions.minimumBatteryDaysRemaining;
      case 'SiteTerminalsWithWorstBestBatteryFluctuation':
        return datasetIndex === 0 && chartView.datasets[datasetIndex].values[index].numericValue > this.props.kpiOptions.maximumBatteryFluctuation;
      case 'SiteTerminalUnlockHistory':
      case 'TerminalUnlockHistory':
        return (datasetIndex === 0 || datasetIndex === 1) && chartView.datasets[datasetIndex].values[index].numericValue > 0;
      case 'SiteTerminalsWithFatalWebUnlocks':
        return true;
      default:
        return false;
    }
  }

  getData(chartView) {
    return {
      labels: chartView.labels.map((l) => l.text[0].text),
      datasets: chartView.datasets.map((ds, datasetIndex) => ({
        label: ds.name.text[0].text,
        data: ds.values.map((v) => v.numericValue),
        borderColor: ds.values.map((v, i) => this.getBorderColor(datasetIndex, this.isRed(chartView, datasetIndex, i), this.isGreen(chartView, datasetIndex, i))),
        backgroundColor: ds.values.map((v, i) => this.getBackgroundColor(datasetIndex, this.isRed(chartView, datasetIndex, i), this.isGreen(chartView, datasetIndex, i))),
        pointRadius: 0,
        pointHitRadius: 20,
        spanGaps: true,
        segment: {
          borderColor: (ctx) => this.getBorderColor(datasetIndex,
              this.isRed(chartView, datasetIndex, ctx.p0DataIndex) ||
            this.isRed(chartView, datasetIndex, ctx.p0DataIndex + 1),
              this.isGreen(chartView, datasetIndex, ctx.p0DataIndex) ||
            this.isGreen(chartView, datasetIndex, ctx.p0DataIndex + 1)),
          backgroundColor: (ctx) => this.getBackgroundColor(datasetIndex,
              this.isRed(chartView, datasetIndex, ctx.p0DataIndex) ||
            this.isRed(chartView, datasetIndex, ctx.p0DataIndex + 1),
              this.isGreen(chartView, datasetIndex, ctx.p0DataIndex) ||
            this.isGreen(chartView, datasetIndex, ctx.p0DataIndex + 1)),
        },
        tension: 0.4,
        borderWidth: 3,
      })),
    };
  }

  makeChart(chartView) {
    return {
      barChart: chartView.barChart,
      options: this.createOptions(chartView),
      data: this.getData(chartView),
    };
  }

  onGraphsPerRowChange(graphsPerRow) {
    const previousGraphsPerRow = cookies.get(GRAPHS_PER_ROW_COOKIE_KEY);
    cookies.set(PREVIOUS_GRAPHS_PER_ROW_COOKIE_KEY, previousGraphsPerRow, {expires: new Date(Date.now() + 31536000000)});
    cookies.set(GRAPHS_PER_ROW_COOKIE_KEY, graphsPerRow, {expires: new Date(Date.now() + 31536000000)});
    this.setState({
      graphsPerRow: graphsPerRow,
    });
  }

  render() {
    const graphsPerRow = this.state.graphsPerRow;

    return (
      <div>
        <div className='active-switch' style={{marginTop: '20px', float: 'left'}}>
          {<div className="btn-group btn-group-devided" data-toggle="buttons">
            <RadioAction
              className={`custom-radio-btn ${this.state.graphsPerRow === '1' ? 'active-btn' : 'inactive-btn'}`}
              title={i18next.t('terminals.graph_layout.one_per_row')}
              groupName="data_type"
              radioId={'1'}
              active={graphsPerRow === '1'}
              onChange={this.onGraphsPerRowChange}
            />
            <RadioAction
              className={`custom-radio-btn ${this.state.graphsPerRow === '2' ? 'active-btn' : 'inactive-btn'}`}
              title={i18next.t('terminals.graph_layout.two_per_row')}
              groupName="data_type"
              radioId={'2'}
              active={graphsPerRow === '2'}
              onChange={this.onGraphsPerRowChange}
            />
            <RadioAction
              className={`custom-radio-btn ${this.state.graphsPerRow === '3' ? 'active-btn' : 'inactive-btn'}`}
              title={i18next.t('terminals.graph_layout.three_per_row')}
              groupName="data_type"
              radioId={'3'}
              active={graphsPerRow === '3'}
              onChange={this.onGraphsPerRowChange}
            />
          </div>}
        </div>
        <div className="col-lg-12" style={{overflow: 'auto'}}>
          {this.props.chartViews.map((chartView) => this.makeChart(chartView)).map((chart, idx) => {
            return (chart.barChart ?
                <div className={ROW_MAPPING[graphsPerRow]} key={idx}>
                  <Bar options={chart.options} data={chart.data}/>
                </div> :
                <div className={ROW_MAPPING[graphsPerRow]} key={idx}>
                  <Line options={chart.options} data={chart.data}/>
                </div>
            );
          })}
        </div>
      </div>
    );
  }
}

CustomChart.propTypes = {
  chartViews: PropTypes.array,
  kpiOptions: PropTypes.object,
  chartsQueryTime: PropTypes.string,
  triggerTabChangeFunc: PropTypes.func,
};

export default CustomChart;
